import React, { useState } from 'react';
import {
  IonModal,
  IonHeader,
  IonToolbar,
  IonTitle,
  IonButtons,
  IonButton,
  IonContent,
  IonSpinner,
  IonText,
  IonIcon,
} from '@ionic/react';
import { exitOutline, caretUp, caretDown, addCircleOutline, removeCircleOutline } from 'ionicons/icons';
import { useQuery } from '@apollo/client/react';
import { PlayerAvatar } from './PlayerAvatar';
import { TeamShield } from './TeamShield';
import { RoleChip } from './RoleChip';
import { FantasyScore } from './FantasyScore';
import { GoalIcon, PenaltyGoalIcon, RefereeCard } from './ui/events';
import { MarketValueHistory } from './MarketValueHistory';
import { SendFeedbackModal } from './SendFeedbackModal';
import { Button } from '../../components/ui/Button';
import { GET_EXTENDED_PLAYER_INFO } from '../../graphql/queries/extendedPlayerInfo';
import { isPlayerInCompetition } from '../../utils/playerCompetition';
import { cn } from '../../lib/utils';
import type {
  GetExtendedPlayerInfoQuery,
  GetExtendedPlayerInfoQueryVariables,
} from '../../types/graphql-generated';

interface ExtendedPlayerInfoModalProps {
  isOpen: boolean;
  onClose: () => void;
  playerId: string;
  // Competition the player is expected to play in (see ReferenceCompetitionContext).
  // When given, a player whose team plays in another competition is flagged as
  // "No está en la liga" next to their team. Without it, only players with no
  // team are flagged.
  referenceCompetitionId?: string;
}

const legendChip =
  'inline-flex items-center gap-1.5 rounded-full border border-[#e8ebf1] bg-white px-2.5 py-1 text-[11px] font-semibold text-slate-600';

const formatMatchDate = (dateString: string) =>
  new Date(dateString).toLocaleString('es-ES', {
    timeZone: 'Europe/Madrid',
    weekday: 'short',
    day: 'numeric',
    month: 'short',
    hour: '2-digit',
    minute: '2-digit',
  });

const RendimientoCell: React.FC<{ teamGoals: number; rivalGoals: number; minutesPlayed: number }> = ({
  teamGoals,
  rivalGoals,
  minutesPlayed,
}) => {
  if (minutesPlayed === 0) return <span className="text-gray-400">-</span>;

  const won = teamGoals > rivalGoals;
  const lost = teamGoals < rivalGoals;
  const color = won ? '#16a34a' : lost ? '#dc2626' : '#374151';

  // The result icon (▲ / ▼ / =) plus the actual score, so it is clear the
  // column shows the scoreline while the player was on the pitch.
  return (
    <span className="inline-flex items-center gap-1 whitespace-nowrap font-semibold" style={{ color }}>
      {won && <IonIcon icon={caretUp} style={{ fontSize: '14px' }} />}
      {lost && <IonIcon icon={caretDown} style={{ fontSize: '14px' }} />}
      {!won && !lost && <span style={{ fontWeight: 'bold' }}>=</span>}
      <span>{teamGoals}-{rivalGoals}</span>
    </span>
  );
};

type MatchPlayerGroup = NonNullable<
  NonNullable<GetExtendedPlayerInfoQuery['getExtendedPlayerInfo']>['matchPlayersPerSeasonCompetition']
>[number];

const SeasonStats: React.FC<{ group: MatchPlayerGroup }> = ({ group }) => {
  const matchPlayers = group.matchPlayers;
  const gamesPlayed = matchPlayers.length;
  const scores = matchPlayers.map((mp) => mp.fantasyScore).filter((s): s is number => s != null);
  const totalScore = scores.length > 0 ? scores.reduce((a, b) => a + b, 0) : null;
  const avgScore = scores.length > 0 ? scores.reduce((a, b) => a + b, 0) / scores.length : null;
  const totalMinutes = matchPlayers.reduce((sum, mp) => sum + mp.minutesPlayed, 0);
  const totalGoals = matchPlayers.reduce((sum, mp) => sum + mp.goals + mp.penaltyGoals, 0);
  const totalYellowCards = matchPlayers.reduce((sum, mp) => sum + mp.yellowCards, 0);
  const totalRedCards = matchPlayers.filter((mp) => mp.redCard).length;

  return (
    <div className="rounded-2xl border border-[#e8ebf1] bg-white p-4 space-y-2">
      <h5 className="font-heading" style={{ margin: '0 0 8px', fontSize: '14px', fontWeight: 800, lineHeight: 1.2 }}>Resumen</h5>
      <div className="flex gap-x-4 gap-y-3 flex-wrap">
        <div className="flex-1 min-w-[90px]">
          <p className="text-xs text-gray-600">Partidos</p>
          <p className="text-lg font-bold">{gamesPlayed}</p>
        </div>
        <div className="flex-1 min-w-[90px]">
          <p className="text-xs text-gray-600">Puntos totales</p>
          <p className="text-lg font-bold">
            {totalScore != null ? totalScore.toFixed(1) : '-'}
          </p>
        </div>
        <div className="flex-1 min-w-[90px]">
          <p className="text-xs text-gray-600">Puntos/partido</p>
          <p className="text-lg font-bold">
            {avgScore != null ? avgScore.toFixed(2) : '-'}
          </p>
        </div>
        <div className="flex-1 min-w-[90px]">
          <p className="text-xs text-gray-600">Minutos</p>
          <p className="text-lg font-bold">{totalMinutes}</p>
        </div>
        <div className="flex-1 min-w-[90px]">
          <p className="text-xs text-gray-600">Goles</p>
          <p className="text-lg font-bold">{totalGoals}</p>
        </div>
        <div className="flex-1 min-w-[90px]">
          <p className="text-xs text-gray-600">Amarillas</p>
          <p className="text-lg font-bold">{totalYellowCards}</p>
        </div>
        <div className="flex-1 min-w-[90px]">
          <p className="text-xs text-gray-600">Rojas</p>
          <p className="text-lg font-bold">{totalRedCards}</p>
        </div>
      </div>
    </div>
  );
};

const formatStat = (value: number | null): string =>
  value != null ? value.toFixed(2) : '-';

// All advanced stats are computed on the backend (see
// RealGamesServices::PlayerCompetitionAdvancedStats); this component just renders them.
const AdvancedStats: React.FC<{ group: MatchPlayerGroup }> = ({ group }) => {
  const [expanded, setExpanded] = useState(false);

  const teamPoints = group.teamPointsPerMatch ?? null;

  // Color the on-pitch / starter averages by how they compare with the team's
  // own points-per-match (only these two share the same win/draw/loss scale):
  // ±10% neutral, then orange / red below and green / blue above.
  const colorClassForRatio = (value: number | null): string => {
    if (value == null || teamPoints == null || teamPoints === 0) return '';

    const diff = (value - teamPoints) / teamPoints;
    if (diff > 0.3) return 'text-blue-600';
    if (diff > 0.1) return 'text-green-600';
    if (diff >= -0.1) return '';
    if (diff >= -0.3) return 'text-orange-500';
    return 'text-red-600';
  };

  const lastFive = group.lastFiveAverage ?? null;
  const onPitch = group.onPitchPointsPerMatch ?? null;
  const starter = group.starterPointsPerMatch ?? null;

  const rows: { label: string; value: React.ReactNode; valueClassName: string }[] = [
    {
      // This average is a fantasy score, so it uses the same badge as the match history table.
      label: 'Media de los últimos 5 partidos',
      value:
        lastFive != null ? (
          <div className="flex justify-end">
            <FantasyScore value={lastFive} />
          </div>
        ) : (
          '-'
        ),
      valueClassName: 'font-semibold',
    },
    {
      label: 'Puntos por partido de equipo',
      value: formatStat(teamPoints),
      valueClassName: 'font-bold',
    },
    {
      label: 'Puntos por partido del equipo estando el jugador en el campo',
      value: formatStat(onPitch),
      valueClassName: `font-semibold ${colorClassForRatio(onPitch)}`,
    },
    {
      label: 'Puntos por partido del equipo cuando el jugador es titular',
      value: formatStat(starter),
      valueClassName: `font-semibold ${colorClassForRatio(starter)}`,
    },
  ];

  return (
    <div className="mt-2">
      <button
        type="button"
        className="flex items-center gap-1 text-sm font-medium text-blue-600"
        onClick={() => setExpanded((prev) => !prev)}
      >
        <IonIcon icon={expanded ? removeCircleOutline : addCircleOutline} />
        {expanded ? 'Ocultar estadísticas avanzadas' : 'Mostrar estadísticas avanzadas'}
      </button>

      {expanded && (
        <div className="mt-2 overflow-hidden rounded-xl border border-[#e8ebf1]">
          <table className="w-full">
            <tbody>
              {rows.map((row, index) => (
                <tr
                  key={row.label}
                  className={`${index > 0 ? 'border-t border-[#eef1f6]' : ''} ${index % 2 === 0 ? 'bg-white' : 'bg-[#f9fafc]'}`}
                >
                  <td
                    className="text-[13px] text-slate-700"
                    style={{ padding: '10px 8px 10px 16px' }}
                  >
                    {row.label}
                  </td>
                  <td
                    className={`whitespace-nowrap text-right text-[13px] ${row.valueClassName}`}
                    style={{ padding: '10px 16px 10px 8px' }}
                  >
                    {row.value}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

const MatchHistoryTable: React.FC<{ group: MatchPlayerGroup }> = ({ group }) => {
  const matchPlayers = group.matchPlayers;

  if (matchPlayers.length === 0) {
    return (
      <p className="text-sm text-center py-4 text-gray-500">
        Sin partidos registrados para esta competición.
      </p>
    );
  }

  const th = 'px-1.5 py-2 text-[10px] font-extrabold uppercase tracking-wide text-slate-500';
  const td = 'px-1.5 py-2 text-[13px]';

  return (
    <>
      <div className="overflow-hidden rounded-xl border border-[#e8ebf1]">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="bg-[#f6f8fb]">
                <th className={`${th} text-center`}>J</th>
                <th className={`${th} text-left`}>Partido</th>
                <th className={`${th} text-center`}>Min</th>
                <th className={`${th} text-center`}>G</th>
                <th className={`${th} text-center`}>Res.Jug</th>
                <th className={`${th} text-center`}>T</th>
                <th className={`${th} text-center`}>Pts</th>
              </tr>
            </thead>
            <tbody>
              {matchPlayers.map((mp, index) => {
                const homeTeam = mp.match.homeTeam.shortName || 'Local';
                const awayTeam = mp.match.awayTeam.shortName || 'Visit.';

                return (
                  <tr
                    key={mp.id}
                    className={`border-t border-[#eef1f6] ${index % 2 === 0 ? 'bg-white' : 'bg-[#f9fafc]'}`}
                  >
                    <td className={`${td} text-center font-heading font-bold text-slate-500`}>
                      {mp.match.matchWeek.weekNumber}
                    </td>
                    <td className={`${td} text-xs leading-tight`}>
                      <div>{homeTeam} {mp.match.goalsHome}</div>
                      <div>{awayTeam} {mp.match.goalsAway}</div>
                    </td>
                    <td className={`${td} text-center text-slate-600`}>{mp.minutesPlayed}'</td>
                    <td className={`${td} text-center`}>
                      <div className="flex flex-wrap items-center justify-center gap-0.5">
                        {Array.from({ length: mp.goals }).map((_, i) => (
                          <GoalIcon key={`g-${i}`} />
                        ))}
                        {Array.from({ length: mp.penaltyGoals }).map((_, i) => (
                          <PenaltyGoalIcon key={`pg-${i}`} />
                        ))}
                      </div>
                    </td>
                    <td className={`${td} text-center`}>
                      <div className="flex items-center justify-center">
                        <RendimientoCell
                          teamGoals={mp.teamGoals}
                          rivalGoals={mp.rivalGoals}
                          minutesPlayed={mp.minutesPlayed}
                        />
                      </div>
                    </td>
                    <td className={`${td} text-center`}>
                      <div className="flex flex-wrap items-center justify-center gap-0.5">
                        {Array.from({ length: mp.yellowCards }).map((_, i) => (
                          <RefereeCard key={`y-${i}`} color="yellow" />
                        ))}
                        {mp.redCard && <RefereeCard color="red" />}
                      </div>
                    </td>
                    <td className={`${td} text-center`}>
                      <div className="flex justify-center">
                        {mp.fantasyScore != null ? <FantasyScore value={mp.fantasyScore} /> : '-'}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      <div className="mt-3 space-y-2">
        <p className="text-xs text-slate-500">
          <span className="font-semibold text-slate-700">Res.Jug:</span> resultado del partido con el jugador en el campo.
        </p>
        <div className="flex flex-wrap gap-2">
          <span className={legendChip}><GoalIcon /> Gol</span>
          <span className={legendChip}><PenaltyGoalIcon /> Penalti</span>
          <span className={legendChip}><IonIcon icon={caretUp} style={{ color: '#16a34a' }} /> Ganó</span>
          <span className={legendChip}><IonIcon icon={caretDown} style={{ color: '#dc2626' }} /> Perdió</span>
          <span className={legendChip}><span style={{ color: '#374151', fontWeight: 'bold' }}>=</span> Empató</span>
          <span className={legendChip}><RefereeCard color="yellow" /> Amarilla</span>
          <span className={legendChip}><RefereeCard color="red" /> Roja</span>
        </div>
      </div>
    </>
  );
};

export const ExtendedPlayerInfoModal = ({
  isOpen,
  onClose,
  playerId,
  referenceCompetitionId,
}: ExtendedPlayerInfoModalProps) => {
  const [selectedSeasonCode, setSelectedSeasonCode] = useState<string | null>(null);
  const [feedbackOpen, setFeedbackOpen] = useState(false);

  const { data, loading, error } = useQuery<
    GetExtendedPlayerInfoQuery,
    GetExtendedPlayerInfoQueryVariables
  >(GET_EXTENDED_PLAYER_INFO, {
    variables: { playerId, seasonCode: selectedSeasonCode ?? undefined },
    skip: !isOpen,
  });

  const playerInfo = data?.getExtendedPlayerInfo;
  const player = playerInfo?.player;
  const allSeasons = playerInfo?.allSeasons ?? [];
  const groups = playerInfo?.matchPlayersPerSeasonCompetition ?? [];

  // Derive the highlighted season: user-selected, or the active one from the response
  const effectiveSeasonCode = selectedSeasonCode ?? allSeasons.find((s) => s.active)?.code ?? null;

  const handleClose = () => {
    setSelectedSeasonCode(null);
    onClose();
  };

  return (
    <IonModal isOpen={isOpen} onDidDismiss={handleClose}>
      <IonHeader>
        <IonToolbar>
          <IonTitle className="ml-4">Información del jugador</IonTitle>
          <IonButtons slot="end">
            <IonButton onClick={handleClose}>Cerrar</IonButton>
          </IonButtons>
        </IonToolbar>
      </IonHeader>
      <IonContent className="ion-padding">
        {loading && (
          <div className="flex items-center justify-center py-8">
            <IonSpinner />
          </div>
        )}

        {error && (
          <div className="py-4">
            <IonText color="danger">
              <p>Error al cargar la información del jugador.</p>
            </IonText>
          </div>
        )}

        {!loading && !error && player && (
          <div className="space-y-4">
            {/* Player Info Section */}
            <div className="rounded-2xl border border-[#e8ebf1] bg-white p-4">
              <div className="flex gap-4 items-start">
                <div className="flex flex-col items-center">
                  <PlayerAvatar image={player.image} nick={player.nick} />
                  <p className="mt-2 font-bold text-center">{player.nick}</p>
                </div>

                <div className="flex-1 space-y-3">
                  <div>
                    <p className="text-xs text-gray-600 mb-1">Nombre completo</p>
                    <p className="font-semibold">
                      {player.name && player.lastName
                        ? `${player.name} ${player.lastName}`
                        : player.name || player.lastName || '-'}
                    </p>
                  </div>

                  <div>
                    <p className="text-xs text-gray-600 mb-1">Posición</p>
                    <RoleChip role={player.role} variant="full" />
                  </div>

                  <div>
                    <p className="text-xs text-gray-600 mb-1">Equipo</p>
                    {player.team && (
                      <div className="flex items-center gap-2">
                        <TeamShield team={player.team} size="medium" />
                        <span className="font-medium">{player.team.fullName}</span>
                      </div>
                    )}
                    {!isPlayerInCompetition(player.team, referenceCompetitionId) && (
                      <span
                        className={cn('text-red-600 inline-flex items-center gap-1', player.team && 'mt-1 text-sm')}
                      >
                        <IonIcon icon={exitOutline} className="text-base" />
                        <span>No está en la liga</span>
                      </span>
                    )}
                  </div>
                </div>
              </div>
            </div>

            <MarketValueHistory
              marketValue={player.marketValue}
              previousMarketValue={player.previousMarketValue}
              history={playerInfo.marketValueHistory}
            />

            {/* Next Match Section */}
            {playerInfo?.nextMatch && (
              <div>
                <h3 className="font-heading" style={{ margin: '0 0 12px', fontSize: '16px', fontWeight: 800, lineHeight: 1.2 }}>Próximo partido</h3>
                <div className="rounded-2xl border border-[#e8ebf1] bg-white p-4">
                  <div className="flex items-center justify-between gap-2 mb-2">
                    <div className="flex items-center gap-2 flex-1">
                      {playerInfo.nextMatch.homeTeam.shield && (
                        <img
                          src={playerInfo.nextMatch.homeTeam.shield}
                          alt={playerInfo.nextMatch.homeTeam.nameShow}
                          className="w-6 h-6 object-contain"
                        />
                      )}
                      <span className="font-medium text-sm">{playerInfo.nextMatch.homeTeam.nameShow}</span>
                    </div>
                    <span className="font-bold text-gray-500 text-sm min-w-[32px] text-center">vs</span>
                    <div className="flex items-center gap-2 flex-1 justify-end">
                      <span className="font-medium text-sm">{playerInfo.nextMatch.awayTeam.nameShow}</span>
                      {playerInfo.nextMatch.awayTeam.shield && (
                        <img
                          src={playerInfo.nextMatch.awayTeam.shield}
                          alt={playerInfo.nextMatch.awayTeam.nameShow}
                          className="w-6 h-6 object-contain"
                        />
                      )}
                    </div>
                  </div>
                  <div className="text-center text-xs text-gray-500">
                    {formatMatchDate(playerInfo.nextMatch.matchDate)}
                  </div>
                </div>
              </div>
            )}

            {/* Season selector */}
            {allSeasons.length > 0 && (
              <div className="flex justify-center items-center gap-2 text-sm pt-1">
                {allSeasons.map((season, i) => (
                  <React.Fragment key={season.id}>
                    {i > 0 && <span className="text-gray-400">·</span>}
                    <button
                      className={
                        season.code === effectiveSeasonCode
                          ? 'font-bold text-blue-600'
                          : 'text-gray-500'
                      }
                      onClick={() => setSelectedSeasonCode(season.code)}
                    >
                      {season.code}
                    </button>
                  </React.Fragment>
                ))}
              </div>
            )}

            {/* Per-competition sections */}
            {groups.length === 0 ? (
              <div className="text-center py-6 text-gray-600">
                <p>No hay partidos registrados para esta temporada.</p>
              </div>
            ) : (
              groups.map((group) => (
                <div key={group.seasonCompetition.id}>
                  <h4 className="font-heading text-slate-700" style={{ margin: '8px 0', fontSize: '14px', fontWeight: 800, lineHeight: 1.2 }}>
                    {group.seasonCompetition.competition.displayName}
                  </h4>
                  <SeasonStats group={group} />
                  <AdvancedStats group={group} />
                  <div className="mt-3">
                    <h5 className="font-heading" style={{ margin: '0 0 8px', fontSize: '14px', fontWeight: 800, lineHeight: 1.2 }}>Historial de partidos</h5>
                    <MatchHistoryTable group={group} />
                  </div>
                </div>
              ))
            )}

            {/* Send feedback */}
            <div className="pt-2 pb-4">
              <Button variant="outline" fullWidth onClick={() => setFeedbackOpen(true)}>
                Enviar comentario
              </Button>
            </div>
          </div>
        )}
      </IonContent>

      <SendFeedbackModal
        isOpen={feedbackOpen}
        onClose={() => setFeedbackOpen(false)}
        entityType="PLAYER"
        entityId={playerId}
      />
    </IonModal>
  );
};
